import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBoxArchive, faArrowLeft, faRotateLeft, faTrash,
  faCircleInfo, faTriangleExclamation
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../context/AuthContext';
import { getArchivedMinistries, unarchiveMinistry, deleteMinistry } from '../services/api';
import { formatAccountingCurrency, formatDate } from '../utils/formatters';
import './AdminMinistries.css';

const ArchivedMinistries = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [ministries, setMinistries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedMinistry, setSelectedMinistry] = useState(null);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/');
      return;
    }
    loadArchivedMinistries();
  }, [isAdmin, navigate]);

  const loadArchivedMinistries = async () => {
    try {
      setLoading(true);
      const response = await getArchivedMinistries(new Date().getFullYear());
      setMinistries(response.data);
    } catch (error) {
      console.error('Erro ao carregar ministérios arquivados:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenRestoreModal = (ministry) => {
    setSelectedMinistry(ministry);
    setShowRestoreModal(true);
  };

  const handleOpenDeleteModal = (ministry) => {
    setSelectedMinistry(ministry);
    setShowDeleteModal(true);
  };

  const handleRestore = async () => {
    if (!selectedMinistry) return;

    try {
      await unarchiveMinistry(selectedMinistry._id);
      await loadArchivedMinistries();
      setShowRestoreModal(false);
      setSelectedMinistry(null);
      alert('Ministério restaurado com sucesso!');
    } catch (error) {
      console.error('Erro ao restaurar ministério:', error);
      alert(error.response?.data?.message || 'Erro ao restaurar ministério.');
    }
  };

  const handleDelete = async () => {
    if (!selectedMinistry) return;

    try {
      const response = await deleteMinistry(selectedMinistry._id);
      await loadArchivedMinistries();
      setShowDeleteModal(false);
      setSelectedMinistry(null);
      
      if (response.data.filesDeleted > 0) {
        alert(`Ministério deletado permanentemente!\n${response.data.filesDeleted} arquivo(s) removido(s) do servidor.`);
      } else {
        alert('Ministério deletado permanentemente!');
      }
    } catch (error) {
      console.error('Erro ao deletar ministério:', error);
      alert(error.response?.data?.message || 'Erro ao deletar ministério.');
    }
  };

  if (loading) {
    return <div className="loading">Carregando...</div>;
  }

  return (
    <div className="admin-page">
      <div className="container">
        <div className="page-header">
          <div>
            <h1><FontAwesomeIcon icon={faBoxArchive} /> Arquivo Morto</h1>
            <p className="text-muted">Ministérios arquivados</p>
          </div>
          <button 
            onClick={() => navigate('/admin/ministries')} 
            className="btn btn-secondary"
          >
            <FontAwesomeIcon icon={faArrowLeft} /> Voltar para Ministérios
          </button>
        </div>

        {ministries.length === 0 ? (
          <div className="card empty-state">
            <p>Nenhum ministério arquivado.</p>
          </div>
        ) : (
          <div className="ministries-table-container">
            <div className="table-responsive">
              <table>
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Líder</th>
                    <th className="hide-mobile">CPF/CNPJ</th>
                    <th className="hide-mobile">Arquivado em</th>
                    <th className="text-right">Saldo Atual</th>
                    <th className="text-center">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {ministries.map((ministry) => (
                    <tr key={ministry._id}>
                      <td>
                        <strong>{ministry.name}</strong>
                      </td>
                      <td>{ministry.leader.name}</td>
                      <td className="hide-mobile text-muted">{ministry.leader.cpfCnpj}</td>
                      <td className="hide-mobile text-muted">{formatDate(ministry.archivedAt)}</td>
                      <td className="text-right">
                        <span className={ministry.currentBalance < 0 ? 'text-negative' : 'text-positive'}>
                          {formatAccountingCurrency(ministry.currentBalance)}
                        </span>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button
                            onClick={() => handleOpenRestoreModal(ministry)}
                            className="btn btn-sm btn-success"
                            title="Restaurar ministério para a lista ativa"
                            aria-label="Restaurar ministério"
                          >
                            <FontAwesomeIcon icon={faRotateLeft} />
                          </button>
                          <button
                            onClick={() => handleOpenDeleteModal(ministry)}
                            className="btn btn-sm btn-danger"
                            title="Deletar permanentemente (irreversível)"
                            aria-label="Deletar permanentemente"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {showRestoreModal && selectedMinistry && (
          <div className="modal-overlay" onClick={() => setShowRestoreModal(false)}>
            <div className="modal-content modal-small" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2><FontAwesomeIcon icon={faRotateLeft} /> Restaurar Ministério</h2>
                <button className="modal-close" onClick={() => setShowRestoreModal(false)}>
                  ×
                </button>
              </div>
              <div className="modal-body">
                <p>Tem certeza que deseja restaurar o ministério <strong>{selectedMinistry.name}</strong>?</p>
                <div className="alert alert-info">
                  <FontAwesomeIcon icon={faCircleInfo} /> O ministério voltará à lista ativa e poderá ser usado normalmente.
                </div>
              </div>
              <div className="modal-footer">
                <button
                  onClick={() => setShowRestoreModal(false)}
                  className="btn btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleRestore}
                  className="btn btn-success"
                >
                  Restaurar
                </button>
              </div>
            </div>
          </div>
        )}

        {showDeleteModal && selectedMinistry && (
          <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
            <div className="modal-content modal-small" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2><FontAwesomeIcon icon={faTrash} /> Deletar Permanentemente</h2>
                <button className="modal-close" onClick={() => setShowDeleteModal(false)}>
                  ×
                </button>
              </div>
              <div className="modal-body">
                <p>Tem certeza que deseja <strong>DELETAR PERMANENTEMENTE</strong> o ministério <strong>{selectedMinistry.name}</strong>?</p>
                <div className="alert alert-danger">
                  <FontAwesomeIcon icon={faTriangleExclamation} /> <strong>Atenção!</strong> Esta ação é irreversível!
                  <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                    <li>Todos os reembolsos serão deletados</li>
                    <li>Todos os arquivos (notas fiscais) serão removidos do servidor</li>
                    <li>Todo o histórico será perdido</li>
                  </ul>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="btn btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleDelete}
                  className="btn btn-danger"
                >
                  Deletar Permanentemente
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ArchivedMinistries;
